/**
 * WebSocket hook for Hyperliquid market data
 * Subscribes to assetCtxs for real-time price updates (no user address needed)
 */

import { WebSocket } from 'partysocket';
import { useCallback, useEffect, useRef, useState } from 'react';

import { HYPERLIQUID_CONFIG } from '../constants';
import type { Market, Hip3DexId } from '../types';
import { HIP3_DEX_IDS } from '../types';

/** Type for assetCtxs subscription response */
interface AssetCtxsMessage {
  channel: 'assetCtxs';
  data: {
    dex: string;
    ctxs: Market[];
  };
}

/** Type for error response */
interface ErrorResponse {
  error: string;
  message?: string;
}

export interface UseMarketDataWebSocketParams {
  /** Callback when asset contexts are received */
  onAssetCtxs: (dex: string, ctxs: Market[]) => void;
  /** Throttle interval for updates (ms) */
  updateThrottleInMs: number;
  /** Whether to enable the WebSocket connection */
  enabled?: boolean;
}

export interface UseMarketDataWebSocketReturn {
  /** Connection state */
  isConnected: boolean;
  /** Error message if any */
  error: string | null;
  /** Reconnect function */
  reconnect: () => void;
}

/**
 * Manages WebSocket connection for Hyperliquid market data.
 * Subscribes to assetCtxs for real-time price updates.
 * Does not require any user address - market data is global.
 */
export function useMarketDataWebSocket({
  onAssetCtxs,
  updateThrottleInMs,
  enabled = true,
}: UseMarketDataWebSocketParams): UseMarketDataWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onAssetCtxsRef = useRef(onAssetCtxs);

  // Keep ref in sync with callback
  onAssetCtxsRef.current = onAssetCtxs;

  // Throttling for updates
  const pendingCtxsRef = useRef(new Map<string, Market[]>());
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store throttle interval in ref to avoid recreating callbacks
  const updateThrottleInMsRef = useRef(updateThrottleInMs);
  updateThrottleInMsRef.current = updateThrottleInMs;

  const flushPendingData = useCallback(() => {
    flushTimeoutRef.current = null;
    pendingCtxsRef.current.forEach((ctxs, dex) => {
      onAssetCtxsRef.current(dex, ctxs);
    });
    pendingCtxsRef.current.clear();
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current !== null) {
      return;
    }
    flushTimeoutRef.current = setTimeout(
      flushPendingData,
      updateThrottleInMsRef.current
    );
  }, [flushPendingData]);

  // Cleanup flush timeout on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current !== null) {
        clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }
    };
  }, []);

  // Store scheduleFlush in ref to keep handleMessage stable
  const scheduleFlushRef = useRef(scheduleFlush);
  scheduleFlushRef.current = scheduleFlush;

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data) as {
        channel?: string;
        data?: unknown;
      };

      if (message.channel === 'subscriptionResponse') {
        return;
      }

      if (message.channel === 'error') {
        const errorPayload = message.data as ErrorResponse;
        const errorMessage =
          errorPayload?.error || errorPayload?.message || 'Unknown error';
        console.error('[HyperLiquid WS] Error:', errorMessage);
        setError(errorMessage);
        return;
      }

      if (message.channel === 'assetCtxs') {
        const data = (message as AssetCtxsMessage).data;
        // Queue the update with throttling
        pendingCtxsRef.current.set(data.dex || '', data.ctxs);
        scheduleFlushRef.current();
      }
    } catch (err) {
      console.error('[HyperLiquid WS] Failed to parse message:', err);
    }
  }, []);

  // Connect on mount (only once when enabled)
  useEffect(() => {
    if (!enabled) return;

    // Don't reconnect if already connected
    if (wsRef.current) {
      const readyState = wsRef.current.readyState;
      if (
        readyState === WebSocket.OPEN ||
        readyState === WebSocket.CONNECTING
      ) {
        return;
      }
    }

    try {
      const ws = new WebSocket(HYPERLIQUID_CONFIG.WS_URL, [], {
        minReconnectionDelay: HYPERLIQUID_CONFIG.RECONNECT_DELAY_BASE,
        maxReconnectionDelay:
          HYPERLIQUID_CONFIG.RECONNECT_DELAY_BASE *
          Math.pow(2, HYPERLIQUID_CONFIG.MAX_RECONNECT_ATTEMPTS - 1),
        reconnectionDelayGrowFactor: 2,
        maxRetries: HYPERLIQUID_CONFIG.MAX_RECONNECT_ATTEMPTS,
      });

      wsRef.current = ws;
      setError(null);

      ws.addEventListener('open', () => {
        setIsConnected(true);
        setError(null); // Clear any stale error state on successful reconnect

        // Start heartbeat
        heartbeatIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ method: 'ping' }));
          }
        }, HYPERLIQUID_CONFIG.HEARTBEAT_INTERVAL);

        // Subscribe to main dex assetCtxs (empty string = main dex)
        ws.send(
          JSON.stringify({
            method: 'subscribe',
            subscription: { type: 'assetCtxs', dex: '' },
          })
        );

        // Subscribe to HIP-3 dexes
        HIP3_DEX_IDS.forEach((dex: Hip3DexId) => {
          ws.send(
            JSON.stringify({
              method: 'subscribe',
              subscription: { type: 'assetCtxs', dex },
            })
          );
        });
      });

      ws.addEventListener('message', handleMessage);

      ws.addEventListener('error', (event) => {
        console.error('[HyperLiquid WS] WebSocket error:', event);
        setError('WebSocket connection error');
      });

      ws.addEventListener('close', () => {
        setIsConnected(false);

        // Clear heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      });
    } catch (err) {
      console.error('[HyperLiquid WS] Failed to create WebSocket:', err);
      setError('Failed to create WebSocket connection');
    }

    return () => {
      if (wsRef.current) {
        try {
          wsRef.current.close(1000, 'Provider unmounted');
        } catch (err) {
          console.warn('[HyperLiquid WS] Error on cleanup:', err);
        }
        wsRef.current = null;
      }

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [enabled, handleMessage]);

  const reconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual reconnect');
      wsRef.current = null;
    }
    // Force re-run of effect by toggling a flag would be complex,
    // so we just close and let partysocket handle reconnection
  }, []);

  return {
    isConnected,
    error,
    reconnect,
  };
}
